import "@webscopeio/react-textarea-autocomplete/style.css";
import { Checkbox, Dropdown, List } from "office-ui-fabric-react";
import React from "react";
import { CreateCommentMutation } from "gql/mutations/comment.graphql";
import { checkPermission } from "../../../../admin/utils";
import { contentTypeOf, reportIdFromPath, trackCommentCreated } from "../../../../lib/analytics";
import { gqlMutationSilent, withContext } from "../../../../common/lib";
import { pluralize } from "../../../../common/utils";
import BaseComponent from "../../../BaseComponent";
import { CommentManager } from "../../../contexts";
import { Comment, CommentableObject } from "../../../wrappers/Comment";
import { CommentEntry } from "./CommentEntry";
import { CommentInput } from "./CommentInput";

const checkboxStyles = () => {
  return {
    root: {
      marginTop: "10px"
    }
  };
};

interface CommentProps {
  object: CommentableObject
  field: string
  inlineState?: boolean
  filterType?: string | boolean
  onChangeType?: (type: string) => void
  autoFocus?: boolean
}

export const FilterTypeOptions = [
  { key: "all", text: "All" },
  { key: "open", text: "Open" },
  { key: "resolved", text: "Resolved" }
];

export class Comments extends BaseComponent<CommentProps> {
  state = {
    showAll: false,
    deleteAnyComment: false,
    edittingId: null as string | null
  };

  private readonly commentsList = React.createRef<HTMLDivElement>();
  private lastCommentsLength = 0;
  onCommentsUpdate = () => this.forceUpdate();
  private unsub: any;

  constructor(props) {
    super(props);

    checkPermission("delete_any_comment").then((result) => {
      if (result) {
        this.setState({ deleteAnyComment: true });
      }
    });
  }

  shouldComponentUpdate(nextProps: any, nextState: any) {
    return JSON.stringify(this.props.object.comments) == JSON.stringify(nextProps.object.comments);
  }

  componentDidMount() {
    this.unsub = [
      PubSub.subscribe("update.Comment", (e, msg) => {
        if (!msg) {
          return;
        }

        PubSub.publish(`update.${msg.commentable_type}:${msg.commentable_id}.comments`, true);
        this.forceUpdate();
      })
    ];
  }

  componentWillUnmount() {
    this.unsub.map(PubSub.unsubscribe);
  }

  handleCommit(mutateFunc, value) {
    if (!value) {
      return;
    }

    const input = {
      gid: this.props.object.gid,
      field: this.props.field,
      comment: value
    };

    mutateFunc({ variables: { input } }).then(() => {
      trackCommentCreated({
        report_id: reportIdFromPath(),
        content_type: contentTypeOf(this.props.object),
        commentable_gid: this.props.object.gid,
        field: this.props.field,
        comment_length: value.length
      });
      if (this.props.filterType === "resolved") {
        this.handleChangeType("all");
      }
    });
  }

  scrollToBottom(force = false) {
    const me = this;
    const func = function() {
      if (me.commentsList.current) {
        const list = jQuery(me.commentsList.current);
        list.scrollTop(list.prop("scrollHeight"));
      }
    };

    setTimeout(func, 100);
  }

  toggleUnresolved() {
    this.setState({ showAll: !this.state.showAll });
  }

  handleChangeType(type: string) {
    const { onChangeType, filterType } = this.props;
    if (!onChangeType || filterType === type) {
      return;
    }

    onChangeType(type);
  }

  render() {
    if (!this.props.filterType) {
      return null;
    }
    return withContext(CommentManager, (commentManager) =>
      this.renderInner(commentManager.for_object(this.props.object))
    );
  }

  renderInner(comments) {
    const { inlineState, filterType, onChangeType } = this.props;

    comments = comments.filter((x) => x.field == this.props.field);
    const resolvedComments = comments.filter((x) => x.resolved).length;
    const showTextBox = inlineState === undefined || inlineState;

    return (
      <div className='px-24 py-16 comments-wrapper bg-xlightgray'>
        <div className='max-w-750px'>
          {!filterType && resolvedComments > 0 && (
            <Checkbox
              label={`Show ${resolvedComments} resolved ${pluralize("comment", resolvedComments)}`}
              defaultChecked={false}
              onChange={() => this.toggleUnresolved()}
              styles={checkboxStyles}
            />
          )}
          {showTextBox && this.renderInput()}
          {comments.length > 0 && (
            <>
              {onChangeType && this.renderFilter()}
              {this.renderList(comments)}
            </>
          )}
        </div>
      </div>
    );
  }

  renderList(comments) {
    const { filterType } = this.props;
    const { edittingId, deleteAnyComment } = this.state;

    let filteredComments = [...comments];

    if (!filterType && !this.state.showAll) {
      filteredComments = filteredComments.filter((x) => !x.resolved);
    }
    if (this.lastCommentsLength != filteredComments.length) {
      this.scrollToBottom(true);
    }
    this.lastCommentsLength = filteredComments.length;

    if (filterType) {
      if (filterType == "open") {
        filteredComments = filteredComments.filter((x) => x.resolved == false);
      } else if (filterType === "resolved") {
        filteredComments = filteredComments.filter((x) => x.resolved);
      }
    }

    filteredComments = filteredComments.sort((a, b) => {
      if (a.resolved && !b.resolved) {
        return 1;
      } else if (!a.resolved && b.resolved) {
        return -1;
      }
      return a.created_at < b.created_at ? 1 : -1;
    });

    if (filteredComments.length === 0) {
      return <div className='text-darkgray mt-1em'>0 {filterType} comments</div>;
    }

    return (
      <List
        items={filteredComments}
        onRenderCell={(comment: Comment) => (
          <CommentEntry
            comment={comment}
            canDeleteAny={deleteAnyComment}
            editting={comment.id === edittingId}
            onEdit={() => this.setState({ edittingId: comment.id })}
            onClose={() => this.setState({ edittingId: null })}
            autoFocus={this.props.autoFocus ?? true}
          />
        )}
      />
    );
  }

  renderFilter() {
    const { filterType } = this.props;

    return (
      <div className='relative flex items-center mt-1em'>
        <span className='mr-16 font-bold text-14'>Comments</span>
        <Dropdown
          className='comments-filter'
          selectedKey={filterType as string}
          options={FilterTypeOptions}
          onChange={(_, option) => this.handleChangeType(option?.key as string)}
          styles={{
            dropdownOptionText: {
              fontSize: 12
            }
          }}
        />
      </div>
    );
  }

  renderInput() {
    return gqlMutationSilent(CreateCommentMutation, (mutateFunc) => (
      <CommentInput
        className='comment-add px-12 py-10 bg-white rounded-5px'
        value=''
        onSave={(value) => this.handleCommit(mutateFunc, value)}
        autoFocus={this.props.autoFocus ?? true}
      />
    ));
  }
}
